<template>
  <div class="svg-container">
    <transition-group name="fade">
      <svg
        :viewBox="viewBox"
        class="svg-content"
        id="inkomstgrupper-svg"
        v-show="!showTaxes"
        key="mainSVG"
      >
      </svg>
      <svg
        :viewBox="viewBox"
        class="taxes-svg"
        v-show="showTaxes"
        key="taxSVG"
      >
        <transition-group name="fade" tag="g">
          <g v-if="showEvenMoreTaxes" key="allTaxesTotal">
            <g v-for="(group, key, index) in allTaxes" :key="key" :transform="`translate(0, ${tax2height(group.offset)})`">
              <rect
                :width="columnWidth"
                :height="tax2height(group.sum)"
                :fill="index === showCategories ? colors.gray : colors.deselected"
                :stroke="colors.background"
              />
              <transition name="fade" tag="g">
                <text :x="columnWidth + 20" :y="tax2height(group.sum / 2)" dominant-baseline="middle" v-if="key !== 'total' && index <= showCategories">
                  {{ group.namn }}
                </text>
              </transition>
            </g>
          </g>
          <g v-if="showAllTaxes" key="allTaxes">
            <rect
              :width="columnWidth"
              :height="columnWidth"
              :fill="colors.gray"
            />
            <text x="10" :y="columnWidth + 20" v-if="!showEvenMoreTaxes">
              {{ allTaxes.income.sum.toLocaleString("sv-FI") }} €
            </text>
          </g>
          <g key="yourTaxes">
            <rect 
              :width="tax2area(inkomstgrupper[incomeGroup].taxes)"
              :height="tax2area(inkomstgrupper[incomeGroup].taxes)"
              :fill="colors.selected"
            />
            <text x="10" :y="tax2area(inkomstgrupper[incomeGroup].taxes) + 20" v-if="!showEvenMoreTaxes">
              {{ inkomstgrupper[incomeGroup].taxes.toLocaleString("sv-FI") }} €
            </text>
          </g>
        </transition-group>
      </svg>
    </transition-group>
  </div>
</template>

<script>
const d3 = Object.assign(
  {},
  require('d3-array'),
  require('d3-axis'),
  require('d3-scale'),
  require('d3-selection'),
);

import aldersgrupper from "../aldersgrupper.json";

let svg;

export default {
  name: 'SvgContent',
  props: {
    allTaxes: Object,
    currentView: String,
    incomeGroup: Number,
    inkomstgrupper: Array,
    showTaxes: Boolean,
    showAllTaxes: Boolean,
    showEvenMoreTaxes: Boolean,
    showCategories: Number,
    colors: Object,
    coloredDots: String,
  },
  data() {
    return {
      aldersgrupper,
      dim: {
        width: 500,
        height: 550,
      },
      graphLayer: null,
      axisLayer: null,
      xScale: null,
      yScale: null,
      yAxisObj: null,
      radius: 3,
      mar: 0.6,
      barWidth: null,
    }
  },
  computed: {
    viewBox() {
      return `0 0 ${this.dim.width} ${this.dim.height}`;
    },
    columnWidth() {
      return this.tax2area(this.allTaxes.income.sum);
    },
    groups() {
      return this.inkomstgrupper.reduce((acc, cv) => {
        acc.push(cv.total_dots);
        return acc;
      }, []);
    },
    taxScale() {
      if (this.showEvenMoreTaxes) {
        return d3
          .scaleSqrt()
          .domain([0, this.allTaxes.income.sum])
          .range([0, this.totalScale(this.allTaxes.income.sum)]);
      }
      else return d3
        .scaleSqrt()
        .domain([0, this.allTaxes.income.sum])
        .range([0, this.dim.width]);
    },
    totalScale() {
      return d3.scaleLinear()
        .domain([0, this.allTaxes.total.sum])
        .range([0, this.dim.height]);
    },
    yLocalScale() {
      return d3
        .scaleBand()
        .range([0, this.yScale.bandwidth()])
        .domain(d3.range(this.barWidth));
    },
  },
  mounted() {
    svg = d3.select("#inkomstgrupper-svg");
    this.initD3();
  },
  watch: {
    coloredDots(dots) {
      this.colorDots(dots);
    },
  },
  methods: {
    tax2area(tax) {
      return this.taxScale(tax);
    },
    tax2height(tax) {
      return this.totalScale(tax);
    },
    initD3() {
      const margin = { top: 10, bottom: 10, left: 140, right: 10 };
      this.dim.graphWidth = this.dim.width - margin.left - margin.right;
      this.dim.graphHeight = this.dim.height - margin.top - margin.bottom;
      this.axisLayer = svg.append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");
      this.graphLayer = svg.append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

      this.xScale = d3
        .scaleBand()
        .range([0, this.dim.graphWidth])
        .paddingOuter(0);
      this.yScale = d3
        .scaleBand()
        .range([0, this.dim.graphHeight])
        .paddingOuter(0)
        .paddingInner(0.3);

      const yAxis = d3.axisLeft(this.yScale);

      this.yAxisObj = this.axisLayer
        .append("g")
        .attr("transform", "translate(-10, 0)")
        .attr("class", "axis")
        .call(yAxis);

      const max = d3.max(this.groups);
      const nrow = Math.ceil(
        this.dim.graphWidth / (2 * (this.radius + this.mar))
      );
      this.barWidth = Math.ceil(max / nrow);
      this.xScale.domain(d3.range(nrow));
      this.yScale.domain(
        this.groups.map((d, i) => {
          return i;
        })
      );
      yAxis.tickFormat((d) => {
        return this.inkomstgrupper[d].name;
      });
      this.yAxisObj.call(yAxis);

      let displaydata = d3.range(d3.sum(this.groups)).map(() => {
        return { all: {}, ages: {} };
      }); // create 1000 dots
      let indexMargin = 0;
      this.groups.forEach((group, groupidx) => {
        const agegroups = this.aldersgrupper[groupidx];
        let ageid = 0;
        let agegroupidx = 0;
        for (let i = 0; i < group; ++i) {
          displaydata[indexMargin + i]["all"] = { group: groupidx, idx: i }; // calculate which income group the dot belongs to

          while (ageid >= agegroups[agegroupidx]) {
            ++agegroupidx;
            ageid = 0;
          }

          displaydata[indexMargin + i]["ages"] = {
            group: agegroupidx,
            idx: ageid,
          }; // calculate which age group the dot belongs to
          ++ageid;
        }

        indexMargin += group;
      });

      this.graphLayer
        .selectAll(".dot")
        .data(displaydata)
        .enter()
        .append("circle")
        .attr("class", "dot")
        .attr("r", this.radius)
        .attr("cy", (d) => {
          return d[this.currentView].group != null
            ? this.yScale(d[this.currentView].group) +
                this.yLocalScale(d[this.currentView].idx % this.barWidth) +
                this.radius +
                this.mar
            : this.dim.graphHeight / 2;
        })
        .attr("cx", (d) => {
          return d[this.currentView].group != null
            ? this.xScale(
                Math.floor((d[this.currentView].idx + 0.1) / this.barWidth)
              ) -
                this.radius -
                this.mar
            : 0;
        })
        .style("opacity", (d) => {
          return d[this.currentView].group != null ? 0.8 : 0.0;
        })
        .style("fill", this.colors.selected);
    },
    colorDots(showGroup) {
      this.graphLayer.selectAll(".dot").style("fill", (d) => {
        switch (showGroup) {
          case "more":
            if (d["all"].group > this.incomeGroup) {
              return this.colors.selected;
            } else return this.colors.deselected;
          case "highest":
            if (d["all"].group === this.inkomstgrupper.length - 1) {
              return this.colors.selected;
            } else return this.colors.deselected;
          case "less":
            if (d["all"].group < this.incomeGroup) {
              return this.colors.selected;
            } else return this.colors.deselected;
          case "same":
            if (d["all"].group === this.incomeGroup) {
              return this.colors.selected;
            } else return this.colors.deselected;
          case "genders":
            if (d["all"].group !== this.incomeGroup) {
              return this.colors.deselected;
            } else if (
              d["all"].idx < this.inkomstgrupper[d["all"].group].men_dots
            ) {
              return this.colors.man;
            } else return this.colors.woman;
          case "all_genders":
            if (d["all"].idx < this.inkomstgrupper[d["all"].group].men_dots) {
              return this.colors.man;
            } else return this.colors.woman;
          default:
            return this.colors.selected;
        }
      });
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">

</style>